import React from 'react';
import { NavLink, useParams, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  FileText,
  Settings,
  Building2,
  Building,
  UserCog,
  Calendar,
  CheckCircle,
  CalendarDays,
  CalendarClock,
  BarChart3,
  UserX,
  CreditCard,
  Award,
  ChevronDown,
  ChevronRight,
  Globe,
  Landmark,
  Ticket,
  ShieldCheck,
  FileCheck,
  AlertTriangle,
  Lock,
  Clock,
  FolderDown,
  Layers,
  UsersRound,
  FileInput,
  Scale,
  Wallet,
  Banknote,
  ClipboardList,
  Sliders,
  ClipboardCheck,
  RotateCcw,
  Eye,
  MapPin,
  Briefcase,
  ScrollText,
  HardDrive,
  FileSpreadsheet,
  TestTube,
  Mail,
  TrendingUp,
  Activity,
  Receipt,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { UserRole, RoleDisplayNames, Permission, RoleRoutes } from '@/types/global.types';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { getPlatformSettings } from '@/services/platformSettingsService';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  permission?: Permission | Permission[]; // Single permission or array (any mode)
  /** Hide this entry when the user holds ANY of these permissions (used to
   *  de-duplicate an entry that is also rendered inside a parent menu). */
  hiddenWhenAnyPermission?: Permission[];
  /** Show this entry only when the user holds ANY of these permissions, on top
   *  of `permission` (used for the in-menu twin of the entry above). */
  visibleOnlyWhenAnyPermission?: Permission[];
  children?: NavItem[];
}

/**
 * Permissions that make the "Gestion Pédagogique" menu appear. "Gestion des
 * Attestations" lives inside that menu when it exists, and is promoted to a
 * top-level entry otherwise, so it is never shown twice and never disappears.
 */
const PEDAGOGIQUE_MENU_PERMISSIONS: Permission[] = [
  Permission.FILIERE_VIEW,
  Permission.GROUP_VIEW,
];

const getNavItems = (role: UserRole, tenantId: string | null): NavItem[] => {
  const basePath = tenantId ? `/institution/${tenantId}` : '';

  switch (role) {
    case UserRole.SUPER_ADMIN:
      return [
        { label: 'Tableau de bord', path: '/admin/dashboard', icon: <LayoutDashboard size={20} /> },
        { label: 'Profil Plateforme', path: '/admin/settings', icon: <Building size={20} /> },
        {
          label: 'Gestion des Établissements',
          path: '/admin/etablissements',
          icon: <Landmark size={20} />,
          children: [
            { label: 'Gestion des Établissements', path: '/admin/etablissements/institutions', icon: <Building2 size={20} /> },
            { label: 'Gestion des Utilisateurs', path: '/admin/etablissements/utilisateurs', icon: <Users size={20} /> },
            { label: 'Gestion des Nomenclatures', path: '/admin/etablissements/nomenclature', icon: <Globe size={20} /> },
          ],
        },
        {
          label: 'Facturation',
          path: '/admin/billing',
          icon: <CreditCard size={20} />,
          children: [
            { label: "Plans d'abonnement", path: '/admin/billing/plans', icon: <Layers size={20} /> },
            { label: 'Abonnements', path: '/admin/billing/subscriptions', icon: <CreditCard size={20} /> },
            { label: 'Factures', path: '/admin/billing/invoices', icon: <FileText size={20} /> },
            { label: 'Compte de résultat', path: '/admin/billing/pnl', icon: <Scale size={20} /> },
            { label: 'Gestion des charges', path: '/admin/billing/expenses', icon: <Receipt size={20} /> },
            { label: 'Fournisseurs', path: '/admin/billing/suppliers', icon: <Building2 size={20} /> },
            { label: 'Paramètres TVA', path: '/admin/billing/settings', icon: <Settings size={20} /> },
          ],
        },
        {
          label: 'Communication & CRM',
          path: '/admin/email-crm',
          icon: <Mail size={20} />,
          children: [
            { label: 'Templates', path: '/admin/email-crm/templates', icon: <FileText size={20} /> },
            { label: 'Campagnes Email', path: '/admin/email-crm/campaigns', icon: <Mail size={20} /> },
            // Automatisations hidden for now — route still works at /admin/email-crm/automations
            // { label: 'Automatisations', path: '/admin/email-crm/automations', icon: <Activity size={20} /> },
            { label: 'Leads', path: '/admin/email-crm/contacts', icon: <UsersRound size={20} /> },
          ],
        },
        {
          label: 'Analytics & Rapports',
          path: '/admin/analytics',
          icon: <TrendingUp size={20} />,
          children: [
            { label: "Vue d'ensemble", path: '/admin/analytics/overview', icon: <Activity size={20} /> },
            { label: 'Tests & Couverture', path: '/admin/analytics/tests', icon: <TestTube size={20} /> },
            { label: 'Utilisateurs', path: '/admin/analytics/users', icon: <Users size={20} /> },
            { label: 'Facturation', path: '/admin/analytics/billing', icon: <CreditCard size={20} /> },
            { label: 'Email Marketing', path: '/admin/analytics/email', icon: <Mail size={20} /> },
            { label: 'Engagement', path: '/admin/analytics/engagement', icon: <BarChart3 size={20} /> },
          ],
        },
        {
          label: 'Sécurité & Audit',
          path: '/admin/security',
          icon: <ShieldCheck size={20} />,
          children: [
            { label: "Journal d'Audit", path: '/admin/audit-logs', icon: <Eye size={20} /> },
            { label: 'Sauvegardes & Restauration', path: '/admin/backup', icon: <HardDrive size={20} /> },
            { label: 'Exports institutions', path: '/admin/exports', icon: <FileSpreadsheet size={20} /> },
          ],
        },
      ];

    case UserRole.DIRECTOR_GENERAL:
      return [
        { label: 'Tableau de bord', path: `${basePath}/dg/dashboard`, icon: <LayoutDashboard size={20} /> },
        { label: 'Gestion des Attestations', path: `${basePath}/dg/requests/manage`, icon: <FileInput size={20} />, permission: Permission.REQUEST_VIEW_ALL, hiddenWhenAnyPermission: PEDAGOGIQUE_MENU_PERMISSIONS },
        {
          label: 'Gestion Établissement',
          path: `${basePath}/dg/etablissement`,
          icon: <Landmark size={20} />,
          children: [
            { label: 'Profil Établissement', path: `${basePath}/dg/etablissement/gestion`, icon: <Building2 size={20} />, permission: Permission.INSTITUTION_PROFILE_VIEW },
            { label: 'Bâtiments & Salles', path: `${basePath}/dg/etablissement/campus`, icon: <Layers size={20} />, permission: Permission.INFRASTRUCTURE_VIEW },
            { label: 'Départements', path: `${basePath}/dg/etablissement/departements`, icon: <Globe size={20} />, permission: Permission.DEPARTMENTS_VIEW },
            { label: 'Documents Officiels', path: `${basePath}/dg/etablissement/documents-institutionnels`, icon: <FileCheck size={20} />, permission: Permission.OFFICIAL_DOCUMENTS_VIEW },
          ],
        },
        {
          label: 'Gestion des Utilisateurs',
          path: `${basePath}/dg/utilisateurs`,
          icon: <Users size={20} />,
          children: [
            { label: 'Gestion du Personnel', path: `${basePath}/dg/utilisateurs/personnel`, icon: <UserCog size={20} />, permission: Permission.STAFF_VIEW },
            { label: 'Gestion des Étudiants', path: `${basePath}/dg/utilisateurs/etudiants`, icon: <GraduationCap size={20} />, permission: Permission.STUDENT_VIEW },
            { label: 'Suivi Dossiers', path: `${basePath}/dg/utilisateurs/suivi-dossiers`, icon: <Eye size={20} />, permission: Permission.TRACKING_PROFILE_VIEW },
            { label: 'Documents E-DEP', path: `${basePath}/dg/utilisateurs/documents-edep`, icon: <FolderDown size={20} />, permission: Permission.EDEP_VIEW },
            { label: 'Paramètres', path: `${basePath}/dg/utilisateurs/parametres`, icon: <Sliders size={20} />, permission: Permission.SETTINGS_USERS_VIEW },
          ],
        },
        {
          label: 'Gestion Pédagogique',
          path: `${basePath}/dg/pedagogique`,
          icon: <BookOpen size={20} />,
          children: [
            { label: 'Gestion des Filières', path: `${basePath}/dg/academique/filieres`, icon: <Layers size={20} />, permission: Permission.FILIERE_VIEW },
            { label: 'Gestion des Groupes', path: `${basePath}/dg/academique/groupes`, icon: <UsersRound size={20} />, permission: Permission.GROUP_VIEW },
            { label: 'Gestion des Attestations', path: `${basePath}/dg/requests/manage`, icon: <FileInput size={20} />, permission: Permission.REQUEST_VIEW_ALL, visibleOnlyWhenAnyPermission: PEDAGOGIQUE_MENU_PERMISSIONS },
            { label: 'Paramètres Académiques', path: `${basePath}/dg/academique/parametres-academiques`, icon: <Sliders size={20} />, permission: Permission.ACADEMIC_CONFIG_VIEW },
          ],
        },
        {
          label: 'Gestion Calendrier et Planning',
          path: `${basePath}/dg/calendrier`,
          icon: <CalendarDays size={20} />,
          children: [
            { label: 'Planning Annuel', path: `${basePath}/dg/calendrier/planning-annuel`, icon: <Calendar size={20} />, permission: Permission.CALENDAR_ANNUAL_VIEW },
            { label: 'Emplois du Temps', path: `${basePath}/dg/calendrier/emplois-du-temps`, icon: <CalendarClock size={20} />, permission: Permission.SCHEDULE_VIEW },
            { label: 'Planning Examens', path: `${basePath}/dg/calendrier/examens`, icon: <ClipboardCheck size={20} />, permission: Permission.EXAM_SCHEDULE_VIEW },
            { label: 'Planning Rattrapages', path: `${basePath}/dg/calendrier/rattrapages`, icon: <RotateCcw size={20} />, permission: Permission.RATTRAPAGE_SCHEDULE_VIEW },
            { label: 'Paramètres', path: `${basePath}/dg/calendrier/parametres`, icon: <Settings size={20} />, permission: Permission.CALENDAR_SETTINGS_VIEW },
          ],
        },
        {
          label: 'Notes & Évaluation',
          path: `${basePath}/dg/notes-evaluation`,
          icon: <Award size={20} />,
          children: [
            { label: 'Notes et Rattrapages', path: `${basePath}/dg/academique/notes`, icon: <Award size={20} />, permission: Permission.NOTES_VIEW },
            { label: 'Absences', path: `${basePath}/dg/academique/absences`, icon: <UserX size={20} />, permission: Permission.ATTENDANCE_VIEW },
            { label: 'Jury de Délibération', path: `${basePath}/dg/academique/jury-deliberation`, icon: <Scale size={20} />, permission: Permission.JURY_VIEW },
          ],
        },
        {
          label: 'Qualité & Suivi',
          path: `${basePath}/dg/qualite-suivi`,
          icon: <ClipboardCheck size={20} />,
          children: [
            { label: 'Suivi des Professeurs', path: `${basePath}/dg/academique/suivi-professeurs`, icon: <Eye size={20} />, permission: Permission.PROFESSOR_MONITORING_VIEW },
            { label: 'Évaluation des Cours', path: `${basePath}/dg/academique/evaluation-cours`, icon: <ClipboardCheck size={20} />, permission: Permission.EVALUATION_VIEW },
          ],
        },
        {
          label: 'Gestion Financière',
          path: `${basePath}/dg/gestion-financiere`,
          icon: <Wallet size={20} />,
          permission: Permission.FINANCE_TUITION_VIEW,
          children: [
            {
              label: 'Comptes Bancaires',
              path: `${basePath}/dg/gestion-financiere/comptes-bancaires`,
              icon: <Landmark size={20} />,
              permission: Permission.INSTITUTION_BANK_VIEW,
            },
            {
              label: 'Frais & Relances',
              path: `${basePath}/dg/gestion-financiere/frais-scolarite`,
              icon: <GraduationCap size={20} />,
              permission: Permission.FINANCE_TUITION_VIEW,
            },
            {
              label: 'Gestion des Paiements',
              path: `${basePath}/dg/gestion-financiere/paiements`,
              icon: <CreditCard size={20} />,
              permission: Permission.FINANCE_PAYMENT_VIEW,
            },
            {
              label: 'Paie Permanents',
              path: `${basePath}/dg/gestion-financiere/paie/permanents`,
              icon: <Banknote size={20} />,
              permission: Permission.PAYROLL_PERMANENT_VIEW,
            },
            {
              label: 'Paie Vacataires',
              path: `${basePath}/dg/gestion-financiere/paie/vacataires`,
              icon: <Clock size={20} />,
              permission: Permission.PAYROLL_VACATAIRE_VIEW,
            },
            {
              label: 'Reporting',
              path: `${basePath}/dg/gestion-financiere/reporting`,
              icon: <BarChart3 size={20} />,
              permission: [Permission.FINANCE_REPORT_RECOVERY, Permission.FINANCE_REPORT_PAYROLL, Permission.FINANCE_REPORT_FORECAST],
            },
          ],
        },
      ];

    case UserRole.PEDAGOGICAL_DIRECTOR:
      return [
        { label: 'Tableau de bord', path: `${basePath}/pedagogie/dashboard`, icon: <LayoutDashboard size={20} /> },
        { label: 'Gestion des Attestations', path: `${basePath}/pedagogie/requests/manage`, icon: <FileInput size={20} />, permission: Permission.REQUEST_VIEW_ALL, hiddenWhenAnyPermission: PEDAGOGIQUE_MENU_PERMISSIONS },
        {
          label: 'Gestion Établissement',
          path: `${basePath}/pedagogie/etablissement`,
          icon: <Landmark size={20} />,
          children: [
            { label: 'Profil Établissement', path: `${basePath}/pedagogie/etablissement/gestion`, icon: <Building2 size={20} />, permission: Permission.INSTITUTION_PROFILE_VIEW },
            { label: 'Bâtiments & Salles', path: `${basePath}/pedagogie/etablissement/campus`, icon: <Layers size={20} />, permission: Permission.INFRASTRUCTURE_VIEW },
            { label: 'Départements', path: `${basePath}/pedagogie/etablissement/departements`, icon: <Globe size={20} />, permission: Permission.DEPARTMENTS_VIEW },
            { label: 'Documents Officiels', path: `${basePath}/pedagogie/etablissement/documents-institutionnels`, icon: <FileCheck size={20} />, permission: Permission.OFFICIAL_DOCUMENTS_VIEW },
          ],
        },
        {
          label: 'Gestion des Utilisateurs',
          path: `${basePath}/pedagogie/utilisateurs`,
          icon: <Users size={20} />,
          children: [
            { label: 'Gestion du Personnel', path: `${basePath}/pedagogie/utilisateurs/personnel`, icon: <UserCog size={20} />, permission: Permission.STAFF_VIEW },
            { label: 'Gestion des Étudiants', path: `${basePath}/pedagogie/utilisateurs/etudiants`, icon: <GraduationCap size={20} />, permission: Permission.STUDENT_VIEW },
            { label: 'Suivi Dossiers', path: `${basePath}/pedagogie/utilisateurs/suivi-dossiers`, icon: <Eye size={20} />, permission: Permission.TRACKING_PROFILE_VIEW },
            { label: 'Documents E-DEP', path: `${basePath}/pedagogie/utilisateurs/documents-edep`, icon: <FolderDown size={20} />, permission: Permission.EDEP_VIEW },
            { label: 'Paramètres', path: `${basePath}/pedagogie/utilisateurs/parametres`, icon: <Sliders size={20} />, permission: Permission.SETTINGS_USERS_VIEW },
          ],
        },
        {
          label: 'Gestion Pédagogique',
          path: `${basePath}/pedagogie/pedagogique`,
          icon: <BookOpen size={20} />,
          children: [
            { label: 'Gestion des Filières', path: `${basePath}/pedagogie/academique/filieres`, icon: <Layers size={20} />, permission: Permission.FILIERE_VIEW },
            { label: 'Gestion des Groupes', path: `${basePath}/pedagogie/academique/groupes`, icon: <UsersRound size={20} />, permission: Permission.GROUP_VIEW },
            { label: 'Gestion des Attestations', path: `${basePath}/pedagogie/requests/manage`, icon: <FileInput size={20} />, permission: Permission.REQUEST_VIEW_ALL, visibleOnlyWhenAnyPermission: PEDAGOGIQUE_MENU_PERMISSIONS },
            { label: 'Paramètres Académiques', path: `${basePath}/pedagogie/academique/parametres-academiques`, icon: <Sliders size={20} />, permission: Permission.ACADEMIC_CONFIG_VIEW },
          ],
        },
        {
          label: 'Gestion Calendrier et Planning',
          path: `${basePath}/pedagogie/calendrier`,
          icon: <CalendarDays size={20} />,
          children: [
            { label: 'Planning Annuel', path: `${basePath}/pedagogie/calendrier/planning-annuel`, icon: <Calendar size={20} />, permission: Permission.CALENDAR_ANNUAL_VIEW },
            { label: 'Emplois du Temps', path: `${basePath}/pedagogie/calendrier/emplois-du-temps`, icon: <CalendarClock size={20} />, permission: Permission.SCHEDULE_VIEW },
            { label: 'Planning Examens', path: `${basePath}/pedagogie/calendrier/examens`, icon: <ClipboardCheck size={20} />, permission: Permission.EXAM_SCHEDULE_VIEW },
            { label: 'Planning Rattrapages', path: `${basePath}/pedagogie/calendrier/rattrapages`, icon: <RotateCcw size={20} />, permission: Permission.RATTRAPAGE_SCHEDULE_VIEW },
            { label: 'Paramètres', path: `${basePath}/pedagogie/calendrier/parametres`, icon: <Settings size={20} />, permission: Permission.CALENDAR_SETTINGS_VIEW },
          ],
        },
        {
          label: 'Notes & Évaluation',
          path: `${basePath}/pedagogie/notes-evaluation`,
          icon: <Award size={20} />,
          children: [
            { label: 'Notes et Rattrapages', path: `${basePath}/pedagogie/academique/notes`, icon: <Award size={20} />, permission: Permission.NOTES_VIEW },
            { label: 'Absences', path: `${basePath}/pedagogie/academique/absences`, icon: <UserX size={20} />, permission: Permission.ATTENDANCE_VIEW },
            { label: 'Jury de Délibération', path: `${basePath}/pedagogie/academique/jury-deliberation`, icon: <Scale size={20} />, permission: Permission.JURY_VIEW },
          ],
        },
        {
          label: 'Qualité & Suivi',
          path: `${basePath}/pedagogie/qualite-suivi`,
          icon: <ClipboardCheck size={20} />,
          children: [
            { label: 'Suivi des Professeurs', path: `${basePath}/pedagogie/academique/suivi-professeurs`, icon: <Eye size={20} />, permission: Permission.PROFESSOR_MONITORING_VIEW },
            { label: 'Évaluation des Cours', path: `${basePath}/pedagogie/academique/evaluation-cours`, icon: <ClipboardCheck size={20} />, permission: Permission.EVALUATION_VIEW },
          ],
        },
        {
          label: 'Gestion Financière',
          path: `${basePath}/pedagogie/gestion-financiere`,
          icon: <Wallet size={20} />,
          permission: [Permission.FINANCE_TUITION_VIEW, Permission.FINANCE_PAYMENT_VIEW],
          children: [
            {
              label: 'Frais & Relances',
              path: `${basePath}/pedagogie/gestion-financiere/frais-scolarite`,
              icon: <GraduationCap size={20} />,
              permission: Permission.FINANCE_TUITION_VIEW,
            },
            {
              label: 'Gestion des Paiements',
              path: `${basePath}/pedagogie/gestion-financiere/paiements`,
              icon: <CreditCard size={20} />,
              permission: Permission.FINANCE_PAYMENT_VIEW,
            },
          ],
        },
        { label: 'Ma Paie', path: `${basePath}/pedagogie/ma-paie`, icon: <Wallet size={20} />, permission: Permission.PAYROLL_OWN_VIEW },
      ];

    case UserRole.PROGRAM_HEAD:
      return [
        { label: 'Tableau de bord', path: `${basePath}/filiere/dashboard`, icon: <LayoutDashboard size={20} /> },
        { label: 'Gestion des Attestations', path: `${basePath}/filiere/requests/manage`, icon: <FileInput size={20} />, permission: Permission.REQUEST_VIEW_ALL, hiddenWhenAnyPermission: PEDAGOGIQUE_MENU_PERMISSIONS },
        {
          label: 'Gestion Établissement',
          path: `${basePath}/filiere/etablissement`,
          icon: <Landmark size={20} />,
          children: [
            { label: 'Profil Établissement', path: `${basePath}/filiere/etablissement/gestion`, icon: <Building2 size={20} />, permission: Permission.INSTITUTION_PROFILE_VIEW },
            { label: 'Bâtiments & Salles', path: `${basePath}/filiere/etablissement/campus`, icon: <Layers size={20} />, permission: Permission.INFRASTRUCTURE_VIEW },
            { label: 'Départements', path: `${basePath}/filiere/etablissement/departements`, icon: <Globe size={20} />, permission: Permission.DEPARTMENTS_VIEW },
            { label: 'Documents Officiels', path: `${basePath}/filiere/etablissement/documents-institutionnels`, icon: <FileCheck size={20} />, permission: Permission.OFFICIAL_DOCUMENTS_VIEW },
          ],
        },
        {
          label: 'Gestion des Utilisateurs',
          path: `${basePath}/filiere/utilisateurs`,
          icon: <Users size={20} />,
          children: [
            { label: 'Gestion du Personnel', path: `${basePath}/filiere/utilisateurs/personnel`, icon: <UserCog size={20} />, permission: Permission.STAFF_VIEW },
            { label: 'Gestion des Étudiants', path: `${basePath}/filiere/utilisateurs/etudiants`, icon: <GraduationCap size={20} />, permission: Permission.STUDENT_VIEW },
            { label: 'Suivi Dossiers', path: `${basePath}/filiere/utilisateurs/suivi-dossiers`, icon: <Eye size={20} />, permission: Permission.TRACKING_PROFILE_VIEW },
            { label: 'Documents E-DEP', path: `${basePath}/filiere/utilisateurs/documents-edep`, icon: <FolderDown size={20} />, permission: Permission.EDEP_VIEW },
            { label: 'Paramètres', path: `${basePath}/filiere/utilisateurs/parametres`, icon: <Sliders size={20} />, permission: Permission.SETTINGS_USERS_VIEW },
          ],
        },
        {
          label: 'Gestion Pédagogique',
          path: `${basePath}/filiere/pedagogique`,
          icon: <BookOpen size={20} />,
          children: [
            { label: 'Gestion des Filières', path: `${basePath}/filiere/academique/filieres`, icon: <Layers size={20} />, permission: Permission.FILIERE_VIEW },
            { label: 'Gestion des Groupes', path: `${basePath}/filiere/academique/groupes`, icon: <UsersRound size={20} />, permission: Permission.GROUP_VIEW },
            { label: 'Gestion des Attestations', path: `${basePath}/filiere/requests/manage`, icon: <FileInput size={20} />, permission: Permission.REQUEST_VIEW_ALL, visibleOnlyWhenAnyPermission: PEDAGOGIQUE_MENU_PERMISSIONS },
            { label: 'Paramètres Académiques', path: `${basePath}/filiere/academique/parametres-academiques`, icon: <Sliders size={20} />, permission: Permission.ACADEMIC_CONFIG_VIEW },
          ],
        },
        {
          label: 'Gestion Calendrier et Planning',
          path: `${basePath}/filiere/calendrier`,
          icon: <CalendarDays size={20} />,
          children: [
            { label: 'Planning Annuel', path: `${basePath}/filiere/calendrier/planning-annuel`, icon: <Calendar size={20} />, permission: Permission.CALENDAR_ANNUAL_VIEW },
            { label: 'Emplois du Temps', path: `${basePath}/filiere/calendrier/emplois-du-temps`, icon: <CalendarClock size={20} />, permission: Permission.SCHEDULE_VIEW },
            { label: 'Planning Examens', path: `${basePath}/filiere/calendrier/examens`, icon: <ClipboardCheck size={20} />, permission: Permission.EXAM_SCHEDULE_VIEW },
            { label: 'Planning Rattrapages', path: `${basePath}/filiere/calendrier/rattrapages`, icon: <RotateCcw size={20} />, permission: Permission.RATTRAPAGE_SCHEDULE_VIEW },
            { label: 'Paramètres', path: `${basePath}/filiere/calendrier/parametres`, icon: <Settings size={20} />, permission: Permission.CALENDAR_SETTINGS_VIEW },
          ],
        },
        {
          label: 'Notes & Évaluation',
          path: `${basePath}/filiere/notes-evaluation`,
          icon: <Award size={20} />,
          children: [
            { label: 'Notes et Rattrapages', path: `${basePath}/filiere/academique/notes`, icon: <Award size={20} />, permission: Permission.NOTES_VIEW },
            { label: 'Absences', path: `${basePath}/filiere/academique/absences`, icon: <UserX size={20} />, permission: Permission.ATTENDANCE_VIEW },
            { label: 'Jury de Délibération', path: `${basePath}/filiere/academique/jury-deliberation`, icon: <Scale size={20} />, permission: Permission.JURY_VIEW },
          ],
        },
        {
          label: 'Qualité & Suivi',
          path: `${basePath}/filiere/qualite-suivi`,
          icon: <ClipboardCheck size={20} />,
          children: [
            { label: 'Suivi des Professeurs', path: `${basePath}/filiere/academique/suivi-professeurs`, icon: <Eye size={20} />, permission: Permission.PROFESSOR_MONITORING_VIEW },
            { label: 'Évaluation des Cours', path: `${basePath}/filiere/academique/evaluation-cours`, icon: <ClipboardCheck size={20} />, permission: Permission.EVALUATION_VIEW },
          ],
        },
        {
          label: 'Gestion Financière',
          path: `${basePath}/filiere/gestion-financiere`,
          icon: <Wallet size={20} />,
          permission: [Permission.FINANCE_TUITION_VIEW, Permission.FINANCE_PAYMENT_VIEW],
          children: [
            {
              label: 'Frais & Relances',
              path: `${basePath}/filiere/gestion-financiere/frais-scolarite`,
              icon: <GraduationCap size={20} />,
              permission: Permission.FINANCE_TUITION_VIEW,
            },
            {
              label: 'Gestion des Paiements',
              path: `${basePath}/filiere/gestion-financiere/paiements`,
              icon: <CreditCard size={20} />,
              permission: Permission.FINANCE_PAYMENT_VIEW,
            },
          ],
        },
        { label: 'Ma Paie', path: `${basePath}/filiere/ma-paie`, icon: <Wallet size={20} />, permission: Permission.PAYROLL_OWN_VIEW },
      ];

    case UserRole.ACADEMIC_COORDINATOR:
      return [
        { label: 'Tableau de bord', path: `${basePath}/coordinator/dashboard`, icon: <LayoutDashboard size={20} /> },
        { label: 'Gestion des Attestations', path: `${basePath}/coordinator/requests/manage`, icon: <FileInput size={20} />, permission: Permission.REQUEST_VIEW_ALL, hiddenWhenAnyPermission: PEDAGOGIQUE_MENU_PERMISSIONS },
        {
          label: 'Gestion Établissement',
          path: `${basePath}/coordinator/etablissement`,
          icon: <Landmark size={20} />,
          children: [
            { label: 'Profil Établissement', path: `${basePath}/coordinator/etablissement/gestion`, icon: <Building2 size={20} />, permission: Permission.INSTITUTION_PROFILE_VIEW },
            { label: 'Bâtiments & Salles', path: `${basePath}/coordinator/etablissement/campus`, icon: <Layers size={20} />, permission: Permission.INFRASTRUCTURE_VIEW },
            { label: 'Départements', path: `${basePath}/coordinator/etablissement/departements`, icon: <Globe size={20} />, permission: Permission.DEPARTMENTS_VIEW },
            { label: 'Documents Officiels', path: `${basePath}/coordinator/etablissement/documents-institutionnels`, icon: <FileCheck size={20} />, permission: Permission.OFFICIAL_DOCUMENTS_VIEW },
          ],
        },
        {
          label: 'Gestion des Utilisateurs',
          path: `${basePath}/coordinator/utilisateurs`,
          icon: <Users size={20} />,
          children: [
            { label: 'Gestion du Personnel', path: `${basePath}/coordinator/utilisateurs/personnel`, icon: <UserCog size={20} />, permission: Permission.STAFF_VIEW },
            { label: 'Gestion des Étudiants', path: `${basePath}/coordinator/utilisateurs/etudiants`, icon: <GraduationCap size={20} />, permission: Permission.STUDENT_VIEW },
            { label: 'Suivi Dossiers', path: `${basePath}/coordinator/utilisateurs/suivi-dossiers`, icon: <Eye size={20} />, permission: Permission.TRACKING_PROFILE_VIEW },
            { label: 'Documents E-DEP', path: `${basePath}/coordinator/utilisateurs/documents-edep`, icon: <FolderDown size={20} />, permission: Permission.EDEP_VIEW },
            { label: 'Paramètres', path: `${basePath}/coordinator/utilisateurs/parametres`, icon: <Sliders size={20} />, permission: Permission.SETTINGS_USERS_VIEW },
          ],
        },
        {
          label: 'Gestion Pédagogique',
          path: `${basePath}/coordinator/pedagogique`,
          icon: <BookOpen size={20} />,
          children: [
            { label: 'Gestion des Filières', path: `${basePath}/coordinator/academique/filieres`, icon: <Layers size={20} />, permission: Permission.FILIERE_VIEW },
            { label: 'Gestion des Groupes', path: `${basePath}/coordinator/academique/groupes`, icon: <UsersRound size={20} />, permission: Permission.GROUP_VIEW },
            { label: 'Gestion des Attestations', path: `${basePath}/coordinator/requests/manage`, icon: <FileInput size={20} />, permission: Permission.REQUEST_VIEW_ALL, visibleOnlyWhenAnyPermission: PEDAGOGIQUE_MENU_PERMISSIONS },
            { label: 'Paramètres Académiques', path: `${basePath}/coordinator/academique/parametres-academiques`, icon: <Sliders size={20} />, permission: Permission.ACADEMIC_CONFIG_VIEW },
          ],
        },
        {
          label: 'Gestion Calendrier et Planning',
          path: `${basePath}/coordinator/calendrier`,
          icon: <CalendarDays size={20} />,
          children: [
            { label: 'Planning Annuel', path: `${basePath}/coordinator/calendrier/planning-annuel`, icon: <Calendar size={20} />, permission: Permission.CALENDAR_ANNUAL_VIEW },
            { label: 'Emplois du Temps', path: `${basePath}/coordinator/calendrier/emplois-du-temps`, icon: <CalendarClock size={20} />, permission: Permission.SCHEDULE_VIEW },
            { label: 'Planning Examens', path: `${basePath}/coordinator/calendrier/examens`, icon: <ClipboardCheck size={20} />, permission: Permission.EXAM_SCHEDULE_VIEW },
            { label: 'Planning Rattrapages', path: `${basePath}/coordinator/calendrier/rattrapages`, icon: <RotateCcw size={20} />, permission: Permission.RATTRAPAGE_SCHEDULE_VIEW },
            { label: 'Paramètres', path: `${basePath}/coordinator/calendrier/parametres`, icon: <Settings size={20} />, permission: Permission.CALENDAR_SETTINGS_VIEW },
          ],
        },
        {
          label: 'Notes & Évaluation',
          path: `${basePath}/coordinator/notes-evaluation`,
          icon: <Award size={20} />,
          children: [
            { label: 'Notes et Rattrapages', path: `${basePath}/coordinator/academique/notes`, icon: <Award size={20} />, permission: Permission.NOTES_VIEW },
            { label: 'Absences', path: `${basePath}/coordinator/academique/absences`, icon: <UserX size={20} />, permission: Permission.ATTENDANCE_VIEW },
            { label: 'Jury de Délibération', path: `${basePath}/coordinator/academique/jury-deliberation`, icon: <Scale size={20} />, permission: Permission.JURY_VIEW },
          ],
        },
        {
          label: 'Qualité & Suivi',
          path: `${basePath}/coordinator/qualite-suivi`,
          icon: <ClipboardCheck size={20} />,
          children: [
            { label: 'Suivi des Professeurs', path: `${basePath}/coordinator/academique/suivi-professeurs`, icon: <Eye size={20} />, permission: Permission.PROFESSOR_MONITORING_VIEW },
            { label: 'Évaluation des Cours', path: `${basePath}/coordinator/academique/evaluation-cours`, icon: <ClipboardCheck size={20} />, permission: Permission.EVALUATION_VIEW },
          ],
        },
        {
          label: 'Gestion Financière',
          path: `${basePath}/coordinator/gestion-financiere`,
          icon: <Wallet size={20} />,
          permission: [Permission.FINANCE_TUITION_VIEW, Permission.FINANCE_PAYMENT_VIEW],
          children: [
            {
              label: 'Frais & Relances',
              path: `${basePath}/coordinator/gestion-financiere/frais-scolarite`,
              icon: <GraduationCap size={20} />,
              permission: Permission.FINANCE_TUITION_VIEW,
            },
            {
              label: 'Gestion des Paiements',
              path: `${basePath}/coordinator/gestion-financiere/paiements`,
              icon: <CreditCard size={20} />,
              permission: Permission.FINANCE_PAYMENT_VIEW,
            },
          ],
        },
        { label: 'Ma Paie', path: `${basePath}/coordinator/ma-paie`, icon: <Wallet size={20} />, permission: Permission.PAYROLL_OWN_VIEW },
      ];

    case UserRole.FINANCIAL_MANAGER:
      return [
        { label: 'Tableau de bord', path: `${basePath}/finance/dashboard`, icon: <LayoutDashboard size={20} /> },
        {
          label: 'Gestion Établissement',
          path: `${basePath}/finance/etablissement`,
          icon: <Landmark size={20} />,
          children: [
            { label: 'Profil Établissement', path: `${basePath}/finance/etablissement/gestion`, icon: <Building2 size={20} />, permission: Permission.INSTITUTION_PROFILE_VIEW },
            { label: 'Bâtiments & Salles', path: `${basePath}/finance/etablissement/campus`, icon: <Layers size={20} />, permission: Permission.INFRASTRUCTURE_VIEW },
            { label: 'Départements', path: `${basePath}/finance/etablissement/departements`, icon: <Globe size={20} />, permission: Permission.DEPARTMENTS_VIEW },
            { label: 'Documents Officiels', path: `${basePath}/finance/etablissement/documents-institutionnels`, icon: <FileCheck size={20} />, permission: Permission.OFFICIAL_DOCUMENTS_VIEW },
          ],
        },
        {
          label: 'Gestion des Utilisateurs',
          path: `${basePath}/finance/utilisateurs`,
          icon: <Users size={20} />,
          children: [
            { label: 'Gestion du Personnel', path: `${basePath}/finance/utilisateurs/personnel`, icon: <UserCog size={20} />, permission: Permission.STAFF_VIEW },
            { label: 'Gestion des Étudiants', path: `${basePath}/finance/utilisateurs/etudiants`, icon: <GraduationCap size={20} />, permission: Permission.STUDENT_VIEW },
            { label: 'Suivi Dossiers', path: `${basePath}/finance/utilisateurs/suivi-dossiers`, icon: <Eye size={20} />, permission: Permission.TRACKING_PROFILE_VIEW },
            { label: 'Documents E-DEP', path: `${basePath}/finance/utilisateurs/documents-edep`, icon: <FolderDown size={20} />, permission: Permission.EDEP_VIEW },
            { label: 'Paramètres', path: `${basePath}/finance/utilisateurs/parametres`, icon: <Sliders size={20} />, permission: Permission.SETTINGS_USERS_VIEW },
          ],
        },
        {
          label: 'Gestion Calendrier et Planning',
          path: `${basePath}/finance/calendrier`,
          icon: <CalendarDays size={20} />,
          children: [
            { label: 'Planning Annuel', path: `${basePath}/finance/calendrier/planning-annuel`, icon: <Calendar size={20} />, permission: Permission.CALENDAR_ANNUAL_VIEW },
            { label: 'Emplois du Temps', path: `${basePath}/finance/calendrier/emplois-du-temps`, icon: <CalendarClock size={20} />, permission: Permission.SCHEDULE_VIEW },
            { label: 'Planning Examens', path: `${basePath}/finance/calendrier/examens`, icon: <ClipboardCheck size={20} />, permission: Permission.EXAM_SCHEDULE_VIEW },
            { label: 'Planning Rattrapages', path: `${basePath}/finance/calendrier/rattrapages`, icon: <RotateCcw size={20} />, permission: Permission.RATTRAPAGE_SCHEDULE_VIEW },
            { label: 'Paramètres', path: `${basePath}/finance/calendrier/parametres`, icon: <Settings size={20} />, permission: Permission.CALENDAR_SETTINGS_VIEW },
          ],
        },
        {
          label: 'Gestion Financière',
          path: `${basePath}/finance/gestion-financiere`,
          icon: <Wallet size={20} />,
          children: [
            {
              label: 'Comptes Bancaires',
              path: `${basePath}/finance/comptes-bancaires`,
              icon: <Landmark size={20} />,
              permission: Permission.INSTITUTION_BANK_VIEW,
            },
            {
              label: 'Frais & Relances',
              path: `${basePath}/finance/frais-scolarite`,
              icon: <GraduationCap size={20} />,
              permission: Permission.FINANCE_TUITION_VIEW,
            },
            {
              label: 'Gestion des Paiements',
              path: `${basePath}/finance/paiements`,
              icon: <CreditCard size={20} />,
              permission: Permission.FINANCE_PAYMENT_VIEW,
            },
            {
              label: 'Paie Permanents',
              path: `${basePath}/finance/paie/permanents`,
              icon: <Banknote size={20} />,
              permission: Permission.PAYROLL_PERMANENT_VIEW,
            },
            {
              label: 'Paie Vacataires',
              path: `${basePath}/finance/paie/vacataires`,
              icon: <Clock size={20} />,
              permission: Permission.PAYROLL_VACATAIRE_VIEW,
            },
            {
              label: 'Reporting',
              path: `${basePath}/finance/reporting`,
              icon: <BarChart3 size={20} />,
              permission: [Permission.FINANCE_REPORT_RECOVERY, Permission.FINANCE_REPORT_PAYROLL, Permission.FINANCE_REPORT_FORECAST],
            },
          ],
        },
        { label: 'Gestion des Attestations', path: `${basePath}/finance/requests/manage`, icon: <FileInput size={20} />, permission: Permission.REQUEST_VIEW_ALL },
        { label: 'Ma Paie', path: `${basePath}/finance/ma-paie`, icon: <Wallet size={20} />, permission: Permission.PAYROLL_OWN_VIEW },
      ];

    case UserRole.MARKETING_MANAGER:
      return [
        { label: 'Tableau de bord', path: `${basePath}/marketing/dashboard`, icon: <LayoutDashboard size={20} /> },
        {
          label: 'Gestion Établissement',
          path: `${basePath}/marketing/etablissement`,
          icon: <Landmark size={20} />,
          children: [
            { label: 'Profil Établissement', path: `${basePath}/marketing/etablissement/gestion`, icon: <Building size={20} />, permission: Permission.INSTITUTION_PROFILE_VIEW },
            { label: 'Campus', path: `${basePath}/marketing/etablissement/campus`, icon: <MapPin size={20} />, permission: Permission.INFRASTRUCTURE_VIEW },
            { label: 'Départements', path: `${basePath}/marketing/etablissement/departements`, icon: <Briefcase size={20} />, permission: Permission.DEPARTMENTS_VIEW },
            { label: 'Documents Officiels', path: `${basePath}/marketing/etablissement/documents-institutionnels`, icon: <ScrollText size={20} />, permission: Permission.OFFICIAL_DOCUMENTS_VIEW },
          ],
        },
        {
          label: 'Gestion des Utilisateurs',
          path: `${basePath}/marketing/utilisateurs`,
          icon: <Users size={20} />,
          children: [
            { label: 'Gestion du Personnel', path: `${basePath}/marketing/utilisateurs/personnel`, icon: <UserCog size={20} />, permission: Permission.STAFF_VIEW },
            { label: 'Gestion des Étudiants', path: `${basePath}/marketing/utilisateurs/etudiants`, icon: <GraduationCap size={20} />, permission: Permission.STUDENT_VIEW },
            { label: 'Suivi Dossiers', path: `${basePath}/marketing/utilisateurs/suivi-dossiers`, icon: <Eye size={20} />, permission: Permission.TRACKING_PROFILE_VIEW },
          ],
        },
        {
          label: 'Gestion Calendrier et Planning',
          path: `${basePath}/marketing/calendrier`,
          icon: <CalendarDays size={20} />,
          children: [
            { label: 'Planning Annuel', path: `${basePath}/marketing/calendrier/planning-annuel`, icon: <Calendar size={20} />, permission: Permission.CALENDAR_ANNUAL_VIEW },
          ],
        },
        {
          label: 'Gestion Financière',
          path: `${basePath}/marketing/gestion-financiere`,
          icon: <Wallet size={20} />,
          permission: [Permission.FINANCE_TUITION_VIEW, Permission.FINANCE_PAYMENT_VIEW],
          children: [
            {
              label: 'Frais & Relances',
              path: `${basePath}/marketing/gestion-financiere/frais-scolarite`,
              icon: <GraduationCap size={20} />,
              permission: Permission.FINANCE_TUITION_VIEW,
            },
            {
              label: 'Gestion des Paiements',
              path: `${basePath}/marketing/gestion-financiere/paiements`,
              icon: <CreditCard size={20} />,
              permission: Permission.FINANCE_PAYMENT_VIEW,
            },
          ],
        },
        { label: 'Ma Paie', path: `${basePath}/marketing/ma-paie`, icon: <Wallet size={20} />, permission: Permission.PAYROLL_OWN_VIEW },
      ];

    case UserRole.COMMERCIAL_MANAGER:
      return [
        { label: 'Tableau de bord', path: `${basePath}/commercial/dashboard`, icon: <LayoutDashboard size={20} /> },
        {
          label: 'Gestion Établissement',
          path: `${basePath}/commercial/etablissement`,
          icon: <Landmark size={20} />,
          children: [
            { label: 'Profil Établissement', path: `${basePath}/commercial/etablissement/gestion`, icon: <Building size={20} />, permission: Permission.INSTITUTION_PROFILE_VIEW },
            { label: 'Campus', path: `${basePath}/commercial/etablissement/campus`, icon: <MapPin size={20} />, permission: Permission.INFRASTRUCTURE_VIEW },
            { label: 'Départements', path: `${basePath}/commercial/etablissement/departements`, icon: <Briefcase size={20} />, permission: Permission.DEPARTMENTS_VIEW },
            { label: 'Documents Officiels', path: `${basePath}/commercial/etablissement/documents-institutionnels`, icon: <ScrollText size={20} />, permission: Permission.OFFICIAL_DOCUMENTS_VIEW },
          ],
        },
        {
          label: 'Gestion des Utilisateurs',
          path: `${basePath}/commercial/utilisateurs`,
          icon: <Users size={20} />,
          children: [
            { label: 'Gestion du Personnel', path: `${basePath}/commercial/utilisateurs/personnel`, icon: <UserCog size={20} />, permission: Permission.STAFF_VIEW },
            { label: 'Gestion des Étudiants', path: `${basePath}/commercial/utilisateurs/etudiants`, icon: <GraduationCap size={20} />, permission: Permission.STUDENT_VIEW },
            { label: 'Suivi Dossiers', path: `${basePath}/commercial/utilisateurs/suivi-dossiers`, icon: <Eye size={20} />, permission: Permission.TRACKING_PROFILE_VIEW },
          ],
        },
        {
          label: 'Gestion Calendrier et Planning',
          path: `${basePath}/commercial/calendrier`,
          icon: <CalendarDays size={20} />,
          children: [
            { label: 'Planning Annuel', path: `${basePath}/commercial/calendrier/planning-annuel`, icon: <Calendar size={20} />, permission: Permission.CALENDAR_ANNUAL_VIEW },
          ],
        },
        {
          label: 'Gestion Financière',
          path: `${basePath}/commercial/gestion-financiere`,
          icon: <Wallet size={20} />,
          permission: [Permission.FINANCE_TUITION_VIEW, Permission.FINANCE_PAYMENT_VIEW],
          children: [
            {
              label: 'Frais & Relances',
              path: `${basePath}/commercial/gestion-financiere/frais-scolarite`,
              icon: <GraduationCap size={20} />,
              permission: Permission.FINANCE_TUITION_VIEW,
            },
            {
              label: 'Gestion des Paiements',
              path: `${basePath}/commercial/gestion-financiere/paiements`,
              icon: <CreditCard size={20} />,
              permission: Permission.FINANCE_PAYMENT_VIEW,
            },
          ],
        },
        { label: 'Ma Paie', path: `${basePath}/commercial/ma-paie`, icon: <Wallet size={20} />, permission: Permission.PAYROLL_OWN_VIEW },
      ];

    case UserRole.CALL_CENTER_AGENT:
      return [
        { label: 'Tableau de bord', path: `${basePath}/call-center/dashboard`, icon: <LayoutDashboard size={20} /> },
        { label: 'Ma Paie', path: `${basePath}/call-center/ma-paie`, icon: <Wallet size={20} />, permission: Permission.PAYROLL_OWN_VIEW },
      ];

    case UserRole.BRANCH_MANAGER:
      return [
        { label: 'Tableau de bord', path: `${basePath}/branch/dashboard`, icon: <LayoutDashboard size={20} /> },
        { label: 'Gestion des Attestations', path: `${basePath}/branch/requests/manage`, icon: <FileInput size={20} />, permission: Permission.REQUEST_VIEW_ALL, hiddenWhenAnyPermission: PEDAGOGIQUE_MENU_PERMISSIONS },
        {
          label: 'Gestion Établissement',
          path: `${basePath}/branch/etablissement`,
          icon: <Landmark size={20} />,
          children: [
            { label: 'Profil Établissement', path: `${basePath}/branch/etablissement/gestion`, icon: <Building2 size={20} />, permission: Permission.INSTITUTION_PROFILE_VIEW },
            { label: 'Bâtiments & Salles', path: `${basePath}/branch/etablissement/campus`, icon: <Layers size={20} />, permission: Permission.INFRASTRUCTURE_VIEW },
            { label: 'Départements', path: `${basePath}/branch/etablissement/departements`, icon: <Globe size={20} />, permission: Permission.DEPARTMENTS_VIEW },
            { label: 'Documents Officiels', path: `${basePath}/branch/etablissement/documents-institutionnels`, icon: <FileCheck size={20} />, permission: Permission.OFFICIAL_DOCUMENTS_VIEW },
          ],
        },
        {
          label: 'Gestion des Utilisateurs',
          path: `${basePath}/branch/utilisateurs`,
          icon: <Users size={20} />,
          children: [
            { label: 'Gestion du Personnel', path: `${basePath}/branch/utilisateurs/personnel`, icon: <UserCog size={20} />, permission: Permission.STAFF_VIEW },
            { label: 'Gestion des Étudiants', path: `${basePath}/branch/utilisateurs/etudiants`, icon: <GraduationCap size={20} />, permission: Permission.STUDENT_VIEW },
            { label: 'Suivi Dossiers', path: `${basePath}/branch/utilisateurs/suivi-dossiers`, icon: <Eye size={20} />, permission: Permission.TRACKING_PROFILE_VIEW },
            { label: 'Documents E-DEP', path: `${basePath}/branch/utilisateurs/documents-edep`, icon: <FolderDown size={20} />, permission: Permission.EDEP_VIEW },
            { label: 'Paramètres', path: `${basePath}/branch/utilisateurs/parametres`, icon: <Sliders size={20} />, permission: Permission.SETTINGS_USERS_VIEW },
          ],
        },
        {
          label: 'Gestion Pédagogique',
          path: `${basePath}/branch/pedagogique`,
          icon: <BookOpen size={20} />,
          children: [
            { label: 'Gestion des Filières', path: `${basePath}/branch/academique/filieres`, icon: <Layers size={20} />, permission: Permission.FILIERE_VIEW },
            { label: 'Gestion des Groupes', path: `${basePath}/branch/academique/groupes`, icon: <UsersRound size={20} />, permission: Permission.GROUP_VIEW },
            { label: 'Gestion des Attestations', path: `${basePath}/branch/requests/manage`, icon: <FileInput size={20} />, permission: Permission.REQUEST_VIEW_ALL, visibleOnlyWhenAnyPermission: PEDAGOGIQUE_MENU_PERMISSIONS },
            { label: 'Paramètres Académiques', path: `${basePath}/branch/academique/parametres-academiques`, icon: <Sliders size={20} />, permission: Permission.ACADEMIC_CONFIG_VIEW },
          ],
        },
        {
          label: 'Gestion Calendrier et Planning',
          path: `${basePath}/branch/calendrier`,
          icon: <CalendarDays size={20} />,
          children: [
            { label: 'Planning Annuel', path: `${basePath}/branch/calendrier/planning-annuel`, icon: <Calendar size={20} />, permission: Permission.CALENDAR_ANNUAL_VIEW },
            { label: 'Emplois du Temps', path: `${basePath}/branch/calendrier/emplois-du-temps`, icon: <CalendarClock size={20} />, permission: Permission.SCHEDULE_VIEW },
            { label: 'Planning Examens', path: `${basePath}/branch/calendrier/examens`, icon: <ClipboardCheck size={20} />, permission: Permission.EXAM_SCHEDULE_VIEW },
            { label: 'Planning Rattrapages', path: `${basePath}/branch/calendrier/rattrapages`, icon: <RotateCcw size={20} />, permission: Permission.RATTRAPAGE_SCHEDULE_VIEW },
            { label: 'Paramètres', path: `${basePath}/branch/calendrier/parametres`, icon: <Settings size={20} />, permission: Permission.CALENDAR_SETTINGS_VIEW },
          ],
        },
        {
          label: 'Notes & Évaluation',
          path: `${basePath}/branch/notes-evaluation`,
          icon: <Award size={20} />,
          children: [
            { label: 'Notes et Rattrapages', path: `${basePath}/branch/academique/notes`, icon: <Award size={20} />, permission: Permission.NOTES_VIEW },
            { label: 'Absences', path: `${basePath}/branch/academique/absences`, icon: <UserX size={20} />, permission: Permission.ATTENDANCE_VIEW },
            { label: 'Jury de Délibération', path: `${basePath}/branch/academique/jury-deliberation`, icon: <Scale size={20} />, permission: Permission.JURY_VIEW },
          ],
        },
        {
          label: 'Qualité & Suivi',
          path: `${basePath}/branch/qualite-suivi`,
          icon: <ClipboardCheck size={20} />,
          children: [
            { label: 'Suivi des Professeurs', path: `${basePath}/branch/academique/suivi-professeurs`, icon: <Eye size={20} />, permission: Permission.PROFESSOR_MONITORING_VIEW },
            { label: 'Évaluation des Cours', path: `${basePath}/branch/academique/evaluation-cours`, icon: <ClipboardCheck size={20} />, permission: Permission.EVALUATION_VIEW },
          ],
        },
        {
          label: 'Gestion Financière',
          path: `${basePath}/branch/gestion-financiere`,
          icon: <Wallet size={20} />,
          children: [
            {
              label: 'Comptes Bancaires',
              path: `${basePath}/branch/gestion-financiere/comptes-bancaires`,
              icon: <Landmark size={20} />,
              permission: Permission.INSTITUTION_BANK_VIEW,
            },
            {
              label: 'Frais & Relances',
              path: `${basePath}/branch/gestion-financiere/frais-scolarite`,
              icon: <GraduationCap size={20} />,
              permission: Permission.FINANCE_TUITION_VIEW,
            },
            {
              label: 'Gestion des Paiements',
              path: `${basePath}/branch/gestion-financiere/paiements`,
              icon: <CreditCard size={20} />,
              permission: Permission.FINANCE_PAYMENT_VIEW,
            },
            {
              label: 'Paie Permanents',
              path: `${basePath}/branch/gestion-financiere/paie/permanents`,
              icon: <Banknote size={20} />,
              permission: Permission.PAYROLL_PERMANENT_VIEW,
            },
            {
              label: 'Paie Vacataires',
              path: `${basePath}/branch/gestion-financiere/paie/vacataires`,
              icon: <Clock size={20} />,
              permission: Permission.PAYROLL_VACATAIRE_VIEW,
            },
            {
              label: 'Reporting',
              path: `${basePath}/branch/gestion-financiere/reporting`,
              icon: <BarChart3 size={20} />,
              permission: [Permission.FINANCE_REPORT_RECOVERY, Permission.FINANCE_REPORT_PAYROLL, Permission.FINANCE_REPORT_FORECAST],
            },
          ],
        },
        { label: 'Ma Paie', path: `${basePath}/branch/ma-paie`, icon: <Wallet size={20} />, permission: Permission.PAYROLL_OWN_VIEW },
      ];

    case UserRole.TEACHER:
      return [
        { label: 'Tableau de bord', path: `${basePath}/teacher/dashboard`, icon: <LayoutDashboard size={20} /> },
        {
          label: 'Gestion des Utilisateurs',
          path: `${basePath}/teacher/utilisateurs`,
          icon: <Users size={20} />,
          children: [
            { label: 'Gestion du Personnel', path: `${basePath}/teacher/utilisateurs/personnel`, icon: <UserCog size={20} />, permission: Permission.STAFF_VIEW },
            { label: 'Gestion des Étudiants', path: `${basePath}/teacher/utilisateurs/etudiants`, icon: <GraduationCap size={20} />, permission: Permission.STUDENT_VIEW },
            { label: 'Suivi Dossiers', path: `${basePath}/teacher/utilisateurs/suivi-dossiers`, icon: <Eye size={20} />, permission: Permission.TRACKING_PROFILE_VIEW },
            { label: 'Documents E-DEP', path: `${basePath}/teacher/utilisateurs/documents-edep`, icon: <FolderDown size={20} />, permission: Permission.EDEP_VIEW },
          ],
        },
        {
          label: 'Calendrier',
          path: `${basePath}/teacher/calendrier`,
          icon: <CalendarDays size={20} />,
          children: [
            { label: 'Planning Annuel', path: `${basePath}/teacher/planning-annuel`, icon: <Calendar size={20} />, permission: Permission.CALENDAR_ANNUAL_VIEW },
            { label: 'Emploi du temps', path: `${basePath}/teacher/emploi-du-temps`, icon: <CalendarClock size={20} />, permission: Permission.SCHEDULE_VIEW },
            // Own courses only, decided server-side — no permission needed, like the app
            { label: 'Planning Examens', path: `${basePath}/teacher/planning-examens?type=exams`, icon: <ClipboardList size={20} /> },
            { label: 'Planning Rattrapages', path: `${basePath}/teacher/planning-examens?type=rattrapages`, icon: <RotateCcw size={20} /> },
          ],
        },
        { label: 'Mes Cours', path: `${basePath}/teacher/mes-cours`, icon: <BookOpen size={20} /> },
        { label: 'Ma Paie', path: `${basePath}/teacher/ma-paie`, icon: <Wallet size={20} />, permission: Permission.PAYROLL_OWN_VIEW },
      ];

    case UserRole.STUDENT:
      return [
        { label: 'Tableau de bord', path: `${basePath}/student/dashboard`, icon: <LayoutDashboard size={20} /> },
        { label: 'Mes Cours', path: `${basePath}/student/mes-cours`, icon: <BookOpen size={20} /> },
        { label: 'Relevé de notes', path: `${basePath}/student/transcript`, icon: <Award size={20} /> },
        { label: 'Mon Emploi du temps', path: `${basePath}/student/mon-emploi-du-temps`, icon: <Calendar size={20} /> },
        { label: 'Planning Examens', path: `${basePath}/student/planning-examens`, icon: <ClipboardList size={20} /> },
        { label: 'Planning Rattrapages', path: `${basePath}/student/rattrapages`, icon: <RotateCcw size={20} /> },
        { label: 'Calendrier Academique', path: `${basePath}/student/calendrier-academique`, icon: <CalendarDays size={20} /> },
        { label: 'Mes Demandes', path: `${basePath}/student/requests`, icon: <FileText size={20} /> },
        { label: 'Mon Compte Financier', path: `${basePath}/student/compte-financier`, icon: <Wallet size={20} /> },
      ];

    case UserRole.PARENT:
      return [
        { label: 'Tableau de bord', path: `${basePath}/parent/dashboard`, icon: <LayoutDashboard size={20} /> },
        { label: 'Calendrier Academique', path: `${basePath}/parent/calendrier-academique`, icon: <CalendarDays size={20} /> },
        { label: 'Emploi du temps', path: `${basePath}/parent/emploi-du-temps`, icon: <Calendar size={20} /> },
        { label: 'Présences', path: `${basePath}/parent/presences`, icon: <CheckCircle size={20} /> },
        { label: 'Notes', path: `${basePath}/parent/grades`, icon: <Award size={20} /> },
        { label: 'Planning Examens', path: `${basePath}/parent/planning-examens`, icon: <ClipboardList size={20} /> },
        { label: 'Planning Rattrapages', path: `${basePath}/parent/rattrapages`, icon: <RotateCcw size={20} /> },
        { label: 'Paiements', path: `${basePath}/parent/payments`, icon: <CreditCard size={20} /> },
      ];

    default:
      return [];
  }
};

interface SidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isCollapsed = false }) => {
  // Use separate selectors to ensure proper re-rendering when individual properties change
  const user = useAuthStore((state) => state.user);
  const viewAsRole = useAuthStore((state) => state.viewAsRole);
  const getEffectiveRole = useAuthStore((state) => state.getEffectiveRole);
  // Use a direct selector for profileSubmissionStatus to ensure re-render on change
  const profileStatus = useAuthStore((state) => state.user?.profileSubmissionStatus);
  // Subscribe to permissions changes to ensure sidebar updates when permissions load
  // These values trigger re-renders when permissions are fetched from the API
  const _permissions = useAuthStore((state) => state.permissions);
  const _permissionsLoaded = useAuthStore((state) => state.permissionsLoaded);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  void _permissions; void _permissionsLoaded; // Used for triggering re-render
  const { tenantId } = useParams<{ tenantId: string }>();
  const { hasPermission, hasAnyPermission } = usePermissions();

  // Helper to check permission (supports single or array with "any" mode)
  const checkPermission = (permission: Permission | Permission[] | undefined): boolean => {
    if (!permission) return true;
    if (Array.isArray(permission)) {
      return hasAnyPermission(permission);
    }
    return hasPermission(permission);
  };

  /**
   * Full visibility test for a nav entry: its own permission, plus the
   * hide/show guards used to place an entry either inside a parent menu or at
   * the top level depending on what the user can actually access.
   */
  const isItemVisible = (item: NavItem): boolean => {
    if (!checkPermission(item.permission)) return false;
    if (item.hiddenWhenAnyPermission && hasAnyPermission(item.hiddenWhenAnyPermission)) {
      return false;
    }
    if (item.visibleOnlyWhenAnyPermission && !hasAnyPermission(item.visibleOnlyWhenAnyPermission)) {
      return false;
    }
    return true;
  };
  const { getDashboardPath } = useAuth();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);
  const [logoError, setLogoError] = React.useState(false);
  const [platformLogo, setPlatformLogo] = React.useState<string | null>(null);

  // Reset logo error when user changes (e.g., on login/logout)
  React.useEffect(() => {
    setLogoError(false);
  }, [user?.tenantLogoUrl]);

  // Fetch platform logo for Super Admin
  React.useEffect(() => {
    if (user?.role === UserRole.SUPER_ADMIN) {
      getPlatformSettings()
        .then((settings) => {
          setPlatformLogo(settings.companyLogo);
        })
        .catch((err) => {
          console.error('Failed to fetch platform settings:', err);
        });
    }
  }, [user?.role]);

  // Listen for platform logo updates (from settings page)
  React.useEffect(() => {
    const handleLogoUpdate = (event: CustomEvent<{ logo: string | null }>) => {
      setPlatformLogo(event.detail.logo);
      setLogoError(false);
    };

    window.addEventListener('platform-logo-updated', handleLogoUpdate as EventListener);
    return () => {
      window.removeEventListener('platform-logo-updated', handleLogoUpdate as EventListener);
    };
  }, []);

  const effectiveRole = getEffectiveRole();

  // Check if finance solution is enabled for this tenant
  const isFinanceEnabled = React.useMemo(() => {
    // SUPER_ADMIN always sees everything (for administration purposes)
    if (user?.role === UserRole.SUPER_ADMIN) return true;
    // Check if 'finance' is in the enabled solutions
    return user?.enabledSolutions?.includes('finance') ?? false;
  }, [user?.role, user?.enabledSolutions]);

  // Get nav items and filter based on enabled solutions
  const navItems = React.useMemo(() => {
    if (!effectiveRole) return [];

    let items = getNavItems(effectiveRole, user?.tenantId || tenantId || null);

    // Add "Mes Cours" for staff who also teach (isAlsoProfessor: true)
    // This applies to: DG, Branch Manager, Pedagogical Director, Program Head,
    // Academic Coordinator, Financial Manager, Marketing Manager, Commercial Manager
    const staffRolesWithPossibleTeaching = [
      UserRole.DIRECTOR_GENERAL,
      UserRole.BRANCH_MANAGER,
      UserRole.PEDAGOGICAL_DIRECTOR,
      UserRole.PROGRAM_HEAD,
      UserRole.ACADEMIC_COORDINATOR,
      UserRole.FINANCIAL_MANAGER,
      UserRole.MARKETING_MANAGER,
      UserRole.COMMERCIAL_MANAGER,
    ];

    if (user?.isAlsoProfessor && staffRolesWithPossibleTeaching.includes(effectiveRole)) {
      // Use role-specific route path for mes-cours
      const roleBasePath = RoleRoutes[effectiveRole].replace(':tenantId', user.tenantId || '');
      // Add the professor space (own courses + own timetable) right after "Tableau de bord"
      const dashboardIndex = items.findIndex(item => item.label === 'Tableau de bord');
      const insertIndex = dashboardIndex >= 0 ? dashboardIndex + 1 : 0;
      items = [
        ...items.slice(0, insertIndex),
        {
          label: 'Espace Professeur',
          path: `${roleBasePath}/mes-cours`,
          icon: <GraduationCap size={20} />,
          children: [
            { label: 'Mes Cours', path: `${roleBasePath}/mes-cours`, icon: <BookOpen size={20} /> },
            { label: 'Mon Emploi du Temps', path: `${roleBasePath}/mon-emploi-du-temps`, icon: <CalendarClock size={20} /> },
          ],
        },
        ...items.slice(insertIndex),
      ];
    }

    // Filter out finance-related menus if finance is not enabled
    if (!isFinanceEnabled) {
      // Labels to filter out when finance is not enabled
      const financeMenuLabels = [
        'Gestion Financière',  // Admin finance menu
        'Mon Compte Financier', // Student finance menu
        'Ma Paie',              // Staff payroll menu
        'Paiements',            // Parent payments menu
      ];
      return items.filter(item => !financeMenuLabels.includes(item.label));
    }

    return items;
  }, [effectiveRole, user?.tenantId, user?.isAlsoProfessor, tenantId, isFinanceEnabled]);

  // Roles that are exempt from profile validation
  const exemptRoles = [UserRole.SUPER_ADMIN, UserRole.DIRECTOR_GENERAL, UserRole.BRANCH_MANAGER, UserRole.PEDAGOGICAL_DIRECTOR, UserRole.PARENT];

  // Check if user needs to complete/fix their profile before accessing other menus
  const isProfileRestricted = React.useMemo(() => {
    if (!user) return false;
    // Exempt roles don't have restrictions
    if (exemptRoles.includes(user.role)) return false;

    // REJECTED status always restricts access
    if (profileStatus === 'REJECTED') return true;

    // DRAFT status only restricts if deadline has passed (user is blocked).
    // Professors (TEACHER or staff who also teach) are never locked: they keep
    // attendance, grades and courses and see the deadline banner instead
    // (decision 2026-10-24).
    if (profileStatus === 'DRAFT') {
      if (user.role === UserRole.TEACHER || user.isAlsoProfessor) return false;
      return user.isProfileDeadlineBlocked === true;
    }

    // PENDING_VALIDATION and APPROVED get full access
    return false;
  }, [user, profileStatus]);

  // Check if profile is pending validation (for showing info banner)
  const isPendingValidation = React.useMemo(() => {
    if (!user) return false;
    if (exemptRoles.includes(user.role)) return false;
    return profileStatus === 'PENDING_VALIDATION';
  }, [user, profileStatus]);

  // Get restriction details based on status
  const getRestrictionDetails = () => {
    if (!user) return { title: '', message: '', color: 'amber', icon: Lock };
    switch (profileStatus) {
      case 'DRAFT':
        // DRAFT with deadline blocked
        return {
          title: 'Délai dépassé',
          message: 'Complétez et soumettez votre profil pour retrouver l\'accès complet',
          color: 'red',
          icon: AlertTriangle,
        };
      case 'REJECTED':
        return {
          title: 'Modifications requises',
          message: 'Corrigez les informations demandées puis soumettez à nouveau',
          color: 'red',
          icon: AlertTriangle,
        };
      default:
        return { title: '', message: '', color: 'amber', icon: Lock };
    }
  };

  // Build profile URL based on user role
  const getProfileUrl = () => {
    if (!user) return '/profile';
    const roleRoute = RoleRoutes[user.role];
    if (!roleRoute) return '/profile';
    return roleRoute.replace(':tenantId', user.tenantId || '') + '/profile';
  };

  // Helper to find parent path for a given item path
  const findParentPath = (targetPath: string, items: NavItem[]): string | null => {
    for (const item of items) {
      if (item.children) {
        for (const child of item.children) {
          if (child.path === targetPath) {
            return item.path;
          }
          // Check nested children (3rd level)
          if (child.children) {
            for (const grandchild of child.children) {
              if (grandchild.path === targetPath) {
                return child.path;
              }
            }
          }
        }
      }
    }
    return null;
  };

  // Helper to get all sibling paths at the same level
  const getSiblingPaths = (targetPath: string, items: NavItem[]): string[] => {
    // Check top level
    const topLevelPaths = items.filter(i => i.children).map(i => i.path);
    if (topLevelPaths.includes(targetPath)) {
      return topLevelPaths;
    }

    // Check nested levels
    for (const item of items) {
      if (item.children) {
        const childPaths = item.children.filter(c => c.children).map(c => c.path);
        if (childPaths.includes(targetPath)) {
          return childPaths;
        }
        // Check 3rd level
        for (const child of item.children) {
          if (child.children) {
            const grandchildPaths = child.children.filter(gc => gc.children).map(gc => gc.path);
            if (grandchildPaths.includes(targetPath)) {
              return grandchildPaths;
            }
          }
        }
      }
    }
    return [];
  };

  const toggleExpanded = (path: string) => {
    setExpandedItems((prev) => {
      // If already expanded, collapse it (and its children)
      if (prev.includes(path)) {
        // Remove this path and any children paths
        return prev.filter(p => !p.startsWith(path) || p === path).filter(p => p !== path);
      }

      // Get siblings at the same level to collapse them
      const siblings = getSiblingPaths(path, navItems);

      // Keep parent paths, remove sibling paths, add new path
      const parentPath = findParentPath(path, navItems);
      const newExpanded = prev.filter(p => {
        // Keep if it's a parent of the new path
        if (parentPath && p === parentPath) return true;
        // Keep if it's a grandparent
        const grandparentPath = parentPath ? findParentPath(parentPath, navItems) : null;
        if (grandparentPath && p === grandparentPath) return true;
        // Remove if it's a sibling
        if (siblings.includes(p)) return false;
        // Remove children of siblings
        if (siblings.some(s => p.startsWith(s))) return false;
        return true;
      });

      return [...newExpanded, path];
    });
  };

  // Helper to check if this is a "Gestion des Utilisateurs" menu that needs smart handling
  const isUserManagementMenu = (item: NavItem): boolean => {
    return item.label === 'Gestion des Utilisateurs' &&
           (item.children?.some(c => c.permission === Permission.STAFF_VIEW || c.permission === Permission.STUDENT_VIEW || c.permission === Permission.TRACKING_PROFILE_VIEW || c.permission === Permission.EDEP_VIEW || c.permission === Permission.SETTINGS_USERS_VIEW) ?? false);
  };

  // Helper to check if this is a "Gestion Établissement" menu that needs smart handling
  const isInstitutionManagementMenu = (item: NavItem): boolean => {
    return item.label === 'Gestion Établissement' &&
           (item.children?.some(c => c.permission === Permission.INSTITUTION_PROFILE_VIEW || c.permission === Permission.INFRASTRUCTURE_VIEW || c.permission === Permission.DEPARTMENTS_VIEW || c.permission === Permission.OFFICIAL_DOCUMENTS_VIEW) ?? false);
  };

  // Helper to check if this is a "Gestion Pédagogique" menu that needs smart handling
  const isPedagogicalManagementMenu = (item: NavItem): boolean => {
    return item.label === 'Gestion Pédagogique' &&
           (item.children?.some(c => c.permission === Permission.FILIERE_VIEW || c.permission === Permission.GROUP_VIEW || c.permission === Permission.REQUEST_VIEW_ALL || c.permission === Permission.ACADEMIC_CONFIG_VIEW) ?? false);
  };

  // Helper to check if this is a "Gestion Calendrier et Planning" or "Calendrier" menu that needs smart handling
  const isCalendarPlanningMenu = (item: NavItem): boolean => {
    return (item.label === 'Gestion Calendrier et Planning' || item.label === 'Calendrier') &&
           (item.children?.some(c =>
             c.permission === Permission.CALENDAR_ANNUAL_VIEW ||
             c.permission === Permission.SCHEDULE_VIEW ||
             c.permission === Permission.EXAM_SCHEDULE_VIEW ||
             c.permission === Permission.RATTRAPAGE_SCHEDULE_VIEW ||
             c.permission === Permission.CALENDAR_SETTINGS_VIEW
           ) ?? false);
  };

  // Helper to check if this is a "Qualité & Suivi" menu that needs smart handling
  const isQualiteSuiviMenu = (item: NavItem): boolean => {
    return item.label === 'Qualité & Suivi' &&
           (item.children?.some(c =>
             c.permission === Permission.PROFESSOR_MONITORING_VIEW ||
             c.permission === Permission.EVALUATION_VIEW
           ) ?? false);
  };

  // Filter children based on permissions
  const getVisibleChildren = (item: NavItem): NavItem[] => {
    if (!item.children) return [];
    return item.children.filter(child => isItemVisible(child));
  };

  const renderNavItem = (item: NavItem, depth = 0, isFirstItem = false) => {
    // Check permission if required (for simple items without children)
    if (!isItemVisible(item)) {
      return null;
    }

    // Special handling for "Gestion des Utilisateurs" menus
    if (isUserManagementMenu(item)) {
      const visibleChildren = getVisibleChildren(item);

      // If no children are visible, hide the menu entirely
      if (visibleChildren.length === 0) {
        return null;
      }

      // If only one child is visible, show it as a direct menu item (not nested)
      if (visibleChildren.length === 1) {
        const singleChild = visibleChildren[0];
        const isItemRestricted = isProfileRestricted && !isFirstItem;

        if (isItemRestricted) {
          return (
            <div key={singleChild.path}>
              <div
                className={`
                  relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                  text-gray-400 cursor-not-allowed rounded-lg group
                  hover:bg-gray-50 transition-colors
                  ${depth > 0 ? 'pl-10' : ''}
                `}
                title="Complétez votre profil pour débloquer"
              >
                <span className="opacity-40">{singleChild.icon}</span>
                {!isCollapsed && (
                  <>
                    <span className="flex-1 text-left opacity-60">{singleChild.label}</span>
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                      <Lock size={10} className="text-gray-400" />
                      <span className="text-[10px] text-gray-400 font-medium">Verrouillé</span>
                    </div>
                  </>
                )}
                {isCollapsed && (
                  <div className="absolute -right-1 -top-1 w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
                    <Lock size={8} className="text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          );
        }

        // Show single child as direct menu item
        return (
          <div key={singleChild.path}>
            <NavLink
              to={singleChild.path}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                rounded-lg transition-colors
                ${depth > 0 ? 'pl-10' : ''}
                ${
                  isActive
                    ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
            >
              {singleChild.icon}
              {!isCollapsed && <span>{singleChild.label}</span>}
            </NavLink>
          </div>
        );
      }

      // Both children are visible - show as parent with submenu
      const isExpanded = expandedItems.includes(item.path);
      const isItemRestricted = isProfileRestricted && !isFirstItem;

      if (isItemRestricted) {
        return (
          <div key={item.path}>
            <div
              className={`
                relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                text-gray-400 cursor-not-allowed rounded-lg group
                hover:bg-gray-50 transition-colors
                ${depth > 0 ? 'pl-10' : ''}
              `}
              title="Complétez votre profil pour débloquer"
            >
              <span className="opacity-40">{item.icon}</span>
              {!isCollapsed && (
                <>
                  <span className="flex-1 text-left opacity-60">{item.label}</span>
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                    <Lock size={10} className="text-gray-400" />
                    <span className="text-[10px] text-gray-400 font-medium">Verrouillé</span>
                  </div>
                </>
              )}
              {isCollapsed && (
                <div className="absolute -right-1 -top-1 w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
                  <Lock size={8} className="text-gray-400" />
                </div>
              )}
            </div>
          </div>
        );
      }

      return (
        <div key={item.path}>
          <button
            onClick={() => toggleExpanded(item.path)}
            className={`
              w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium
              text-gray-600 hover:bg-gray-100 hover:text-gray-900
              transition-colors rounded-lg
              ${depth > 0 ? 'pl-10' : ''}
            `}
          >
            {item.icon}
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left">{item.label}</span>
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </>
            )}
          </button>
          {isExpanded && !isCollapsed && (
            <div className="mt-1 space-y-1">
              {visibleChildren.map((child) => renderNavItem(child, depth + 1, false))}
            </div>
          )}
        </div>
      );
    }

    // Special handling for "Gestion Établissement" menus (permission-based visibility)
    if (isInstitutionManagementMenu(item)) {
      const visibleChildren = getVisibleChildren(item);

      // If no children are visible, hide the menu entirely
      if (visibleChildren.length === 0) {
        return null;
      }

      // If only one child is visible, show it as a direct menu item (not nested)
      if (visibleChildren.length === 1) {
        const singleChild = visibleChildren[0];
        const isItemRestricted = isProfileRestricted && !isFirstItem;

        if (isItemRestricted) {
          return (
            <div key={singleChild.path}>
              <div
                className={`
                  relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                  text-gray-400 cursor-not-allowed rounded-lg group
                  hover:bg-gray-50 transition-colors
                  ${depth > 0 ? 'pl-10' : ''}
                `}
                title="Complétez votre profil pour débloquer"
              >
                <span className="opacity-40">{singleChild.icon}</span>
                {!isCollapsed && (
                  <>
                    <span className="flex-1 text-left opacity-60">{singleChild.label}</span>
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                      <Lock size={10} className="text-gray-400" />
                      <span className="text-[10px] text-gray-400 font-medium">Verrouillé</span>
                    </div>
                  </>
                )}
                {isCollapsed && (
                  <div className="absolute -right-1 -top-1 w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
                    <Lock size={8} className="text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          );
        }

        // Show single child as direct menu item
        return (
          <div key={singleChild.path}>
            <NavLink
              to={singleChild.path}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                rounded-lg transition-colors
                ${depth > 0 ? 'pl-10' : ''}
                ${
                  isActive
                    ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
            >
              {singleChild.icon}
              {!isCollapsed && <span>{singleChild.label}</span>}
            </NavLink>
          </div>
        );
      }

      // Multiple children visible - show as parent with submenu
      const isExpanded = expandedItems.includes(item.path);
      const isItemRestricted = isProfileRestricted && !isFirstItem;

      if (isItemRestricted) {
        return (
          <div key={item.path}>
            <div
              className={`
                relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                text-gray-400 cursor-not-allowed rounded-lg group
                hover:bg-gray-50 transition-colors
                ${depth > 0 ? 'pl-10' : ''}
              `}
              title="Complétez votre profil pour débloquer"
            >
              <span className="opacity-40">{item.icon}</span>
              {!isCollapsed && (
                <>
                  <span className="flex-1 text-left opacity-60">{item.label}</span>
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                    <Lock size={10} className="text-gray-400" />
                    <span className="text-[10px] text-gray-400 font-medium">Verrouillé</span>
                  </div>
                </>
              )}
              {isCollapsed && (
                <div className="absolute -right-1 -top-1 w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
                  <Lock size={8} className="text-gray-400" />
                </div>
              )}
            </div>
          </div>
        );
      }

      return (
        <div key={item.path}>
          <button
            onClick={() => toggleExpanded(item.path)}
            className={`
              w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium
              text-gray-600 hover:bg-gray-100 hover:text-gray-900
              transition-colors rounded-lg
              ${depth > 0 ? 'pl-10' : ''}
            `}
          >
            {item.icon}
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left">{item.label}</span>
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </>
            )}
          </button>
          {isExpanded && !isCollapsed && (
            <div className="mt-1 space-y-1">
              {visibleChildren.map((child) => renderNavItem(child, depth + 1, false))}
            </div>
          )}
        </div>
      );
    }

    // Special handling for "Gestion Pédagogique" menus (permission-based visibility)
    if (isPedagogicalManagementMenu(item)) {
      const visibleChildren = getVisibleChildren(item);

      // If no children are visible, hide the menu entirely
      if (visibleChildren.length === 0) {
        return null;
      }

      // If only one child is visible, show it as a direct menu item (not nested)
      if (visibleChildren.length === 1) {
        const singleChild = visibleChildren[0];
        const isItemRestricted = isProfileRestricted && !isFirstItem;

        if (isItemRestricted) {
          return (
            <div key={singleChild.path}>
              <div
                className={`
                  relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                  text-gray-400 cursor-not-allowed rounded-lg group
                  hover:bg-gray-50 transition-colors
                  ${depth > 0 ? 'pl-10' : ''}
                `}
                title="Complétez votre profil pour débloquer"
              >
                <span className="opacity-40">{singleChild.icon}</span>
                {!isCollapsed && (
                  <>
                    <span className="flex-1 text-left opacity-60">{singleChild.label}</span>
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                      <Lock size={10} className="text-gray-400" />
                      <span className="text-[10px] text-gray-400 font-medium">Verrouillé</span>
                    </div>
                  </>
                )}
                {isCollapsed && (
                  <div className="absolute -right-1 -top-1 w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
                    <Lock size={8} className="text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          );
        }

        return renderNavItem(singleChild, depth, isFirstItem);
      }

      // Show parent with all visible children
      const isExpanded = expandedItems.includes(item.path);
      const isItemRestricted = isProfileRestricted && !isFirstItem;

      if (isItemRestricted) {
        return (
          <div key={item.path}>
            <div
              className={`
                relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                text-gray-400 cursor-not-allowed rounded-lg group
                hover:bg-gray-50 transition-colors
                ${depth > 0 ? 'pl-10' : ''}
              `}
              title="Complétez votre profil pour débloquer"
            >
              <span className="opacity-40">{item.icon}</span>
              {!isCollapsed && (
                <>
                  <span className="flex-1 text-left opacity-60">{item.label}</span>
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                    <Lock size={10} className="text-gray-400" />
                    <span className="text-[10px] text-gray-400 font-medium">Verrouillé</span>
                  </div>
                </>
              )}
              {isCollapsed && (
                <div className="absolute -right-1 -top-1 w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
                  <Lock size={8} className="text-gray-400" />
                </div>
              )}
            </div>
          </div>
        );
      }

      return (
        <div key={item.path}>
          <button
            onClick={() => toggleExpanded(item.path)}
            className={`
              w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium
              text-gray-600 hover:bg-gray-100 hover:text-gray-900
              transition-colors rounded-lg
              ${depth > 0 ? 'pl-10' : ''}
            `}
          >
            {item.icon}
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left">{item.label}</span>
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </>
            )}
          </button>
          {isExpanded && !isCollapsed && (
            <div className="mt-1 space-y-1">
              {visibleChildren.map((child) => renderNavItem(child, depth + 1, false))}
            </div>
          )}
        </div>
      );
    }

    // Special handling for "Gestion Calendrier et Planning" menus (permission-based visibility)
    if (isCalendarPlanningMenu(item)) {
      const visibleChildren = getVisibleChildren(item);

      // If no children are visible, hide the menu entirely
      if (visibleChildren.length === 0) {
        return null;
      }

      // If only one child is visible, show it as a direct menu item (not nested)
      if (visibleChildren.length === 1) {
        const singleChild = visibleChildren[0];
        const isItemRestricted = isProfileRestricted && !isFirstItem;

        if (isItemRestricted) {
          return (
            <div key={singleChild.path}>
              <div
                className={`
                  relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                  text-gray-400 cursor-not-allowed rounded-lg group
                  hover:bg-gray-50 transition-colors
                  ${depth > 0 ? 'pl-10' : ''}
                `}
                title="Complétez votre profil pour débloquer"
              >
                <span className="opacity-40">{singleChild.icon}</span>
                {!isCollapsed && (
                  <>
                    <span className="flex-1 text-left opacity-60">{singleChild.label}</span>
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                      <Lock size={10} className="text-gray-400" />
                      <span className="text-[10px] text-gray-400 font-medium">Verrouillé</span>
                    </div>
                  </>
                )}
                {isCollapsed && (
                  <div className="absolute -right-1 -top-1 w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
                    <Lock size={8} className="text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          );
        }

        // Show single child as direct menu item
        return (
          <div key={singleChild.path}>
            <NavLink
              to={singleChild.path}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                rounded-lg transition-colors
                ${depth > 0 ? 'pl-10' : ''}
                ${
                  isActive
                    ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
            >
              {singleChild.icon}
              {!isCollapsed && <span>{singleChild.label}</span>}
            </NavLink>
          </div>
        );
      }

      // Multiple children visible - show as parent with submenu
      const isExpanded = expandedItems.includes(item.path);
      const isItemRestricted = isProfileRestricted && !isFirstItem;

      if (isItemRestricted) {
        return (
          <div key={item.path}>
            <div
              className={`
                relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                text-gray-400 cursor-not-allowed rounded-lg group
                hover:bg-gray-50 transition-colors
                ${depth > 0 ? 'pl-10' : ''}
              `}
              title="Complétez votre profil pour débloquer"
            >
              <span className="opacity-40">{item.icon}</span>
              {!isCollapsed && (
                <>
                  <span className="flex-1 text-left opacity-60">{item.label}</span>
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                    <Lock size={10} className="text-gray-400" />
                    <span className="text-[10px] text-gray-400 font-medium">Verrouillé</span>
                  </div>
                </>
              )}
              {isCollapsed && (
                <div className="absolute -right-1 -top-1 w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
                  <Lock size={8} className="text-gray-400" />
                </div>
              )}
            </div>
          </div>
        );
      }

      return (
        <div key={item.path}>
          <button
            onClick={() => toggleExpanded(item.path)}
            className={`
              w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium
              text-gray-600 hover:bg-gray-100 hover:text-gray-900
              transition-colors rounded-lg
              ${depth > 0 ? 'pl-10' : ''}
            `}
          >
            {item.icon}
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left">{item.label}</span>
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </>
            )}
          </button>
          {isExpanded && !isCollapsed && (
            <div className="mt-1 space-y-1">
              {visibleChildren.map((child) => renderNavItem(child, depth + 1, false))}
            </div>
          )}
        </div>
      );
    }

    // Special handling for "Qualité & Suivi" menus (permission-based visibility)
    if (isQualiteSuiviMenu(item)) {
      const visibleChildren = getVisibleChildren(item);

      // If no children are visible, hide the menu entirely
      if (visibleChildren.length === 0) {
        return null;
      }

      // If only one child is visible, show it as a direct menu item (not nested)
      if (visibleChildren.length === 1) {
        const singleChild = visibleChildren[0];
        const isItemRestricted = isProfileRestricted && !isFirstItem;

        if (isItemRestricted) {
          return (
            <div key={singleChild.path}>
              <div
                className={`
                  relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                  text-gray-400 cursor-not-allowed rounded-lg group
                  hover:bg-gray-50 transition-colors
                  ${depth > 0 ? 'pl-10' : ''}
                `}
                title="Complétez votre profil pour débloquer"
              >
                <span className="opacity-40">{singleChild.icon}</span>
                {!isCollapsed && (
                  <>
                    <span className="flex-1 text-left opacity-60">{singleChild.label}</span>
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                      <Lock size={10} className="text-gray-400" />
                      <span className="text-[10px] text-gray-400 font-medium">Verrouillé</span>
                    </div>
                  </>
                )}
                {isCollapsed && (
                  <div className="absolute -right-1 -top-1 w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
                    <Lock size={8} className="text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          );
        }

        // Show single child as direct menu item
        return (
          <div key={singleChild.path}>
            <NavLink
              to={singleChild.path}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                rounded-lg transition-colors
                ${depth > 0 ? 'pl-10' : ''}
                ${
                  isActive
                    ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
            >
              {singleChild.icon}
              {!isCollapsed && <span>{singleChild.label}</span>}
            </NavLink>
          </div>
        );
      }

      // Multiple children visible - show as parent with submenu
      const isExpanded = expandedItems.includes(item.path);
      const isItemRestricted = isProfileRestricted && !isFirstItem;

      if (isItemRestricted) {
        return (
          <div key={item.path}>
            <div
              className={`
                relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                text-gray-400 cursor-not-allowed rounded-lg group
                hover:bg-gray-50 transition-colors
                ${depth > 0 ? 'pl-10' : ''}
              `}
              title="Complétez votre profil pour débloquer"
            >
              <span className="opacity-40">{item.icon}</span>
              {!isCollapsed && (
                <>
                  <span className="flex-1 text-left opacity-60">{item.label}</span>
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                    <Lock size={10} className="text-gray-400" />
                    <span className="text-[10px] text-gray-400 font-medium">Verrouillé</span>
                  </div>
                </>
              )}
              {isCollapsed && (
                <div className="absolute -right-1 -top-1 w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
                  <Lock size={8} className="text-gray-400" />
                </div>
              )}
            </div>
          </div>
        );
      }

      return (
        <div key={item.path}>
          <button
            onClick={() => toggleExpanded(item.path)}
            className={`
              w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium
              text-gray-600 hover:bg-gray-100 hover:text-gray-900
              transition-colors rounded-lg
              ${depth > 0 ? 'pl-10' : ''}
            `}
          >
            {item.icon}
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left">{item.label}</span>
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </>
            )}
          </button>
          {isExpanded && !isCollapsed && (
            <div className="mt-1 space-y-1">
              {visibleChildren.map((child) => renderNavItem(child, depth + 1, false))}
            </div>
          )}
        </div>
      );
    }

    // Standard handling for other menu items with permission-based filtering
    const visibleChildren = getVisibleChildren(item);
    const hasVisibleChildren = visibleChildren.length > 0;
    const isExpanded = expandedItems.includes(item.path);

    // Check if this item should be restricted (all items except first dashboard)
    const isItemRestricted = isProfileRestricted && !isFirstItem;

    // If item has children but none are visible (no permissions), hide the menu entirely
    if (item.children && item.children.length > 0 && visibleChildren.length === 0) {
      return null;
    }

    // If only one child is visible, show it directly without parent menu
    if (item.children && item.children.length > 0 && visibleChildren.length === 1) {
      const singleChild = visibleChildren[0];

      if (isItemRestricted) {
        return (
          <div key={singleChild.path}>
            <div
              className={`
                relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium
                text-gray-400 cursor-not-allowed rounded-lg group
                hover:bg-gray-50 transition-colors
                ${depth > 0 ? 'pl-10' : ''}
              `}
              title="Complétez votre profil pour débloquer"
            >
              <span className="opacity-40">{singleChild.icon}</span>
              {!isCollapsed && (
                <>
                  <span className="flex-1 text-left opacity-60">{singleChild.label}</span>
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                    <Lock size={10} className="text-gray-400" />
                    <span className="text-[10px] text-gray-400 font-medium">Verrouillé</span>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      }

      return (
        <NavLink
          key={singleChild.path}
          to={singleChild.path}
          className={({ isActive }) => `
            flex items-center gap-3 px-4 py-2.5 text-sm font-medium
            rounded-lg transition-colors
            ${depth > 0 ? 'pl-10' : ''}
            ${
              isActive
                ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }
          `}
        >
          {singleChild.icon}
          {!isCollapsed && <span>{singleChild.label}</span>}
        </NavLink>
      );
    }

    // If restricted, show cute locked version
    if (isItemRestricted) {
      return (
        <div key={item.path}>
          <div
            className={`
              relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium
              text-gray-400 cursor-not-allowed rounded-lg group
              hover:bg-gray-50 transition-colors
              ${depth > 0 ? 'pl-10' : ''}
            `}
            title="Complétez votre profil pour débloquer"
          >
            {/* Faded icon */}
            <span className="opacity-40">{item.icon}</span>
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left opacity-60">{item.label}</span>
                {/* Cute lock indicator */}
                <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                  <Lock size={10} className="text-gray-400" />
                  <span className="text-[10px] text-gray-400 font-medium">Verrouillé</span>
                </div>
              </>
            )}
            {isCollapsed && (
              <div className="absolute -right-1 -top-1 w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
                <Lock size={8} className="text-gray-400" />
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div key={item.path}>
        {hasVisibleChildren ? (
          <button
            onClick={() => toggleExpanded(item.path)}
            className={`
              w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium
              text-gray-600 hover:bg-gray-100 hover:text-gray-900
              transition-colors rounded-lg
              ${depth > 0 ? 'pl-10' : ''}
            `}
          >
            {item.icon}
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left">{item.label}</span>
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </>
            )}
          </button>
        ) : (
          <NavLink
            to={item.path}
            className={({ isActive }) => `
              flex items-center gap-3 px-4 py-2.5 text-sm font-medium
              rounded-lg transition-colors
              ${depth > 0 ? 'pl-10' : ''}
              ${
                isActive
                  ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }
            `}
          >
            {item.icon}
            {!isCollapsed && <span>{item.label}</span>}
          </NavLink>
        )}

        {hasVisibleChildren && isExpanded && !isCollapsed && (
          <div className="mt-1 space-y-1">
            {visibleChildren.map((child) => renderNavItem(child, depth + 1, false))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`
        fixed left-0 top-0 h-full bg-white border-r border-gray-200
        flex flex-col transition-all duration-300 z-40
        ${isCollapsed ? 'w-20' : 'w-64'}
      `}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-center border-b border-gray-200 px-4">
        <button
          onClick={() => navigate(getDashboardPath())}
          className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
        >
          {/* Super Admin: use platform logo */}
          {user?.role === UserRole.SUPER_ADMIN && platformLogo && !logoError ? (
            <img
              src={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${platformLogo}`}
              alt="NEXEDU"
              className="h-14 max-w-[200px] object-contain"
              onError={() => setLogoError(true)}
            />
          ) : /* Other roles: use tenant logo */
          user?.tenantLogoUrl && !logoError ? (
            <img
              src={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${user.tenantLogoUrl}`}
              alt={user.tenantName || 'Logo'}
              className="h-14 max-w-[200px] object-contain"
              onError={() => setLogoError(true)}
            />
          ) : (
            <>
              <div className="h-10 w-10 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-xl font-bold text-white">N</span>
              </div>
              {!isCollapsed && (
                <span className="text-xl font-bold text-gray-900">NEXEDU</span>
              )}
            </>
          )}
        </button>
      </div>

      {/* View As Indicator */}
      {viewAsRole && !isCollapsed && (
        <div className="mx-4 mt-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs text-amber-700">
            <span className="font-medium">Mode aperçu:</span>
            <br />
            {RoleDisplayNames[viewAsRole]}
          </p>
        </div>
      )}

      {/* Profile Restriction Banner - Cute locked state */}
      {isProfileRestricted && !isCollapsed && (
        <div className={`mx-3 mt-3 p-3 rounded-xl border-2 border-dashed transition-all ${
          profileStatus === 'REJECTED'
            ? 'bg-red-50 border-red-300'
            : 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-300'
        }`}>
          <div className="flex items-start gap-3">
            {React.createElement(getRestrictionDetails().icon, {
              size: 20,
              className: profileStatus === 'REJECTED'
                ? 'text-red-500 flex-shrink-0 mt-0.5'
                : 'text-amber-500 flex-shrink-0 mt-0.5'
            })}
            <div className="flex-1">
              <p className={`text-sm font-semibold ${
                profileStatus === 'REJECTED' ? 'text-red-800' : 'text-amber-800'
              }`}>
                {getRestrictionDetails().title}
              </p>
              <p className={`text-xs mt-1 ${
                profileStatus === 'REJECTED' ? 'text-red-600' : 'text-amber-700'
              }`}>
                {getRestrictionDetails().message}
              </p>
              <button
                onClick={() => navigate(getProfileUrl())}
                className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                  profileStatus === 'REJECTED'
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                }`}
              >
                <FileText size={12} />
                Compléter mon profil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Validation Info Banner */}
      {isPendingValidation && !isCollapsed && (
        <div className="mx-3 mt-3 p-3 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
          <div className="flex items-start gap-3">
            <Clock size={18} className="text-blue-500 flex-shrink-0 mt-0.5 animate-pulse" />
            <div>
              <p className="text-sm font-semibold text-blue-800">
                En attente de validation
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Vous avez un accès complet pendant l'examen de votre dossier
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {navItems.map((item, index) => renderNavItem(item, 0, index === 0))}
      </nav>
    </aside>
  );
};
